import { fabric } from 'fabric'
import { Circle } from 'types/fabric/fabric-impl'

export type BaseChange = {
  name: string
  fill?: string
  stroke?: string
}

export type ImageChange = BaseChange & {
  src?: string
}

export type TextChange = BaseChange & {
  text?: string
  textBackgroundColor?: string
}

export type QrCodeChange = BaseChange & {
  content?: string
}

export type AnyChange = BaseChange & Record<string, unknown>

export type ObjectChange = ImageChange | TextChange | QrCodeChange | AnyChange

export async function enlivenObjects(objects: Record<string, unknown>[]) {
  return new Promise<fabric.Object[]>(resolve => {
    fabric.util.enlivenObjects(
      objects,
      (enlivenedObjects: fabric.Object[]) => {
        resolve(enlivenedObjects)
      },
      ''
    )
  })
}

export async function overrideImageChange(
  object: Record<string, unknown>,
  change: ImageChange
): Promise<Record<string, unknown>> {
  // if no src provided, just keep the original object
  const newImageSrc = change.src
  if (newImageSrc === undefined || newImageSrc === '') {
    return object
  }

  return new Promise(async resolve => {
    // enliven the object
    const enlivenedObjects = await enlivenObjects([object])
    const enlivenedImage = enlivenedObjects[0] as fabric.Image
    const oldImage = enlivenedImage

    if (oldImage.getSrc() === newImageSrc) {
      resolve(object)
      return
    }

    const oldWidthBeforeScaling = oldImage.width || 0
    const oldHeightBeforeScaling = oldImage.height || 0
    const oldWidthAfterScaling = oldImage.getScaledWidth()
    const oldHeightAfterScaling = oldImage.getScaledHeight()
    const oldCenterPoint = oldImage.getCenterPoint()
    const oldLeft = oldImage.left || 0
    const oldTop = oldImage.top || 0
    const oldAngle = oldImage.angle || 0
    const oldClipPath = oldImage.clipPath
    const oldShadow = oldImage.shadow

    fabric.Image.fromURL(
      newImageSrc,
      newImage => {
        const newWidthBeforeScaling = newImage.width || 0
        const newHeightBeforeScaling = newImage.height || 0

        const isOldImageVertical = oldWidthBeforeScaling < oldHeightBeforeScaling
        const isOldImageHorizontal = oldWidthBeforeScaling > oldHeightBeforeScaling
        const isNewImageVertical = newWidthBeforeScaling < newHeightBeforeScaling
        const isNewImageHorizontal = newWidthBeforeScaling > newHeightBeforeScaling

        // Scaling the new image to the old image size
        if (isOldImageHorizontal) {
          newImage.scaleToWidth(oldWidthAfterScaling)
        } else if (isOldImageVertical) {
          newImage.scaleToHeight(oldHeightAfterScaling)
        } else {
          if (isNewImageHorizontal) {
            newImage.scaleToHeight(oldHeightAfterScaling)
          } else if (isNewImageVertical) {
            newImage.scaleToWidth(oldWidthAfterScaling)
          } else {
            newImage.scaleToHeight(oldHeightAfterScaling) // scaleToHeight === scaleToWidth
          }
        }

        // need to use our own clipPath to crop image first
        newImage.set({
          clipPath: new fabric.Rect({
            left: oldLeft,
            top: oldTop,
            width: oldWidthAfterScaling,
            height: oldHeightAfterScaling,
            angle: oldAngle,
            absolutePositioned: true
          })
        })

        if (oldShadow) {
          newImage.set({ shadow: oldShadow })
        }

        if (oldClipPath) {
          // We only support circle clip path for now
          if (oldClipPath.type === 'circle') {
            const oldRadius = (oldClipPath as Circle).radius || 0
            const oldScaleX = oldImage.scaleX || 1
            const oldRadiusAfterScaling = oldRadius * oldScaleX // scaleX === scaleY
            const newScaleX = newImage.scaleX || 1

            // x * scaleX = oldRadiusAfterScaling
            // x = oldRadiusAfterScaling / scaleX
            // Crop the image with the right radius
            newImage.set({
              clipPath: new fabric.Circle({
                radius: oldRadiusAfterScaling / newScaleX,
                originX: 'center',
                originY: 'center'
              })
            })
          } else {
            newImage.set({
              clipPath: oldClipPath
            })
          }
        }

        // finally rotate the newImage
        newImage.set({
          angle: oldAngle
        })

        newImage.setPositionByOrigin(oldCenterPoint, 'center', 'center')

        resolve(newImage.toObject())
      },
      {
        crossOrigin: 'anonymous'
      }
    )
  })
}

export async function overrideAnyChange(
  object: Record<string, unknown>,
  change: AnyChange
): Promise<Record<string, unknown>> {
  // Note: this means that user can control more properties of the text object even they are not defined
  return {
    ...object,
    ...change
  }
}

export async function overrideTemplateChanges(
  templateJSON: any,
  changes: ObjectChange[]
): Promise<{
  objects: Record<string, unknown>[]
}> {
  let copiedTemplateJSON

  if (typeof templateJSON === 'string') {
    copiedTemplateJSON = JSON.parse(templateJSON)
  } else {
    copiedTemplateJSON = JSON.parse(JSON.stringify(templateJSON))
  }

  const objects = copiedTemplateJSON.objects as Record<string, unknown>[]

  copiedTemplateJSON.objects = await Promise.all(
    objects.map(async object => {
      const objectName = object.name
      const objectType = object.type
      const foundChange = changes.find(change => change.name === objectName)

      if (foundChange) {
        if (objectType === 'image') {
          return await overrideImageChange(object, foundChange)
        } else {
          // for other kinds of objects, but not supported, just keep the original object
          return await overrideAnyChange(object, foundChange)
        }
      }

      // if not found, just keep the original object
      return object
    })
  )

  return copiedTemplateJSON
}
